import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper, { PaperProps } from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import debounce from "lodash/debounce";

import AutocompleteFormField from "components/autocomplete-form-field";
import { AUTOCOMPLETE_PAGE_SIZE, TagTypes } from "constants/tags";
import { useGetTagsCountQuery, useGetTagsQuery } from "services/tags";
import { Tag } from "services/types/tags.type";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const SEARCH_DEBOUNCE_MS = 250;

type SelectTagProps = {
  label?: string;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  tagType?: TagTypes;
  enablePortal?: boolean;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  sx?: SxProps;
  // Off by default: as a FILTER an inactive tag is still a fair question to ask. A picker that
  // ASSIGNS tags turns it on, because the backend refuses inactive tags.
  activeOnly?: boolean;
};

export type RhfSelectTag<T extends FieldValues> = SelectTagProps &
  UseControllerProps<T>;

export default function SelectTag<T extends FieldValues>({
  control,
  name,
  onOptionSelected,
  tagType,
  label = "By Tag",
  enablePortal,
  openOnFocus = true,
  autoFocus = true,
  sx,
  activeOnly = false
}: RhfSelectTag<T>) {
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const debouncedSetSearch = React.useMemo(
    () => debounce(setDebouncedSearch, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => {
    debouncedSetSearch(searchInput);
  }, [searchInput, debouncedSetSearch]);
  React.useEffect(
    () => () => debouncedSetSearch.cancel(),
    [debouncedSetSearch]
  );

  const countFilterArgs = {
    ...(tagType ? { type: tagType } : {}),
    ...(activeOnly ? { is_active: true } : {}),
    search: debouncedSearch || undefined
  };
  const {
    data: tags = [],
    isLoading,
    isUninitialized
  } = useGetTagsQuery({
    ...countFilterArgs,
    limit: AUTOCOMPLETE_PAGE_SIZE
  });
  const { data: totalCount = 0 } = useGetTagsCountQuery(countFilterArgs);

  // Show "type to search for more" only when the server has more matches
  // than what fits in one page. Comparing against the real totalCount avoids
  // the false positive at exactly AUTOCOMPLETE_PAGE_SIZE tags.
  const isAtCap = totalCount > tags.length;

  // Render the cap helper as a footer of the listbox Paper. Rendering it
  // as a sibling below the field would put it under the popper when the
  // dropdown opens downward (see TagsAutocomplete for the same pattern).
  //
  // Stable reference (empty deps + ref): MUI compares PaperComponent by
  // referential identity. A new function on every `isAtCap` flip would
  // unmount the Paper subtree (losing scroll, flashing the listbox).
  const isAtCapRef = React.useRef(isAtCap);
  isAtCapRef.current = isAtCap;
  const PaperComponent = React.useMemo(() => {
    const Component = ({ children, ...rest }: PaperProps) => (
      <Paper {...rest}>
        {children}
        {isAtCapRef.current && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              px: 1.5,
              py: 0.75,
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            Type to search for more tags
          </Typography>
        )}
      </Paper>
    );
    Component.displayName = "SelectTagListboxPaper";
    return Component;
  }, []);

  const tagColors: Record<string, string> = {};
  tags.forEach((tag) => {
    tagColors[String(tag.id)] = tag.color;
  });

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      isLoading={isLoading || isUninitialized}
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      multiple
      name={name}
      label={label}
      PaperComponent={PaperComponent}
      options={tags.map((tag: Tag) => ({
        label: tag.name,
        value: String(tag.id)
      }))}
      // Server already filtered by ?search= — passthrough disables MUI's
      // client-side re-filter (which would drop substring matches under the
      // previous `matchFrom: "start"` strategy).
      filterOptions={(x) => x}
      // KNOWN STALE-SEARCH BUG (not fixed in this PR — tracked for follow-up):
      // `onTextInputChange` is wired to TextField's DOM `onChange` inside
      // `AutocompleteFormField`. That captures user typing but NOT MUI's
      // programmatic input resets after selection or `clear` — `searchInput`
      // can stay stuck at the last typed value across uses, leaving the next
      // open of the dropdown showing stale server-filtered results. MUI
      // commonly resets input post-selection without firing DOM onChange.
      // Fix path: extend `AutocompleteFormField` to expose MUI's
      // `onInputChange` (with `reason`), then handle `reset`/`clear` here the
      // same way `tags-autocomplete.component.tsx` does. Surfaced in PR #5530
      // review v2; defer to a follow-up that touches the shared form-field
      // wrapper.
      onTextInputChange={setSearchInput}
      getOptionLabel={(option) => {
        return option.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
            />
            <Box
              width={22}
              minHeight={22}
              bgcolor={tagColors[option.value] ?? "#fff"}
              borderRadius="2px"
            />
            <ListItemText>{option.label}</ListItemText>
          </Stack>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 280, ...sx }}
    />
  );
}
